import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  FUNDING_PRODUCT_COLUMN_SPANS,
  FundingFieldSpec,
  getFundingProduct
} from '../../../constants/fundingProducts';

/**
 * Step 5 - funding-product specific detail.
 *
 * The fields shown are driven entirely by the funding product chosen on the previous step, so what
 * is captured at origination is relevant to the funding actually being requested.
 *
 * A few descriptors below point at fields that also appear on step 4 (loanAmount, loanPurpose,
 * primaryRepaymentSource, proposedRepaymentDate, customerContribution). They register the same
 * form field rather than a copy, so there is still a single value behind them - editing in either
 * place updates the same thing. They are repeated here because the product designs show them in
 * context alongside the product-specific fields.
 */


const ProductFundingDetailsStep: React.FC = () => {
  const { register, watch, formState: { errors } } = useFormContext();

  const fundingType = (watch('loan.fundingType' as any) as string) || '';
  const product = getFundingProduct(fundingType);
  const loanErrors = (errors as any)?.loan || {};

  const errorFor = (name: string) => loanErrors?.[name]?.message as string | undefined;

  const renderField = (spec: FundingFieldSpec, span: number) => {
    const path = `loan.${spec.name}`;
    const invalid = errorFor(spec.name) ? 'is-invalid' : '';
    const numeric = spec.kind === 'amount' || spec.kind === 'number';

    const registration = numeric
      ? register(path as any, {
          setValueAs: (v: any) => (v === '' || v === null || v === undefined ? undefined : Number(v))
        })
      : register(path as any);

    return (
      <div className={`col-md-${span}`} key={spec.name}>
        <div className="form-group mb-3">
          <label htmlFor={spec.name} className="form-label">
            {spec.label}{spec.kind === 'amount' ? ' (R)' : ''}
          </label>
          <input
            id={spec.name}
            className={`form-control ${invalid}`}
            type={spec.kind === 'date' ? 'date' : numeric ? 'number' : 'text'}
            {...(spec.kind === 'amount' ? { step: '0.01', min: '0' } : {})}
            {...(spec.kind === 'number' ? { min: '0' } : {})}
            placeholder={spec.placeholder}
            {...registration}
          />
          {spec.hint && !errorFor(spec.name) && <small className="text-muted">{spec.hint}</small>}
          {errorFor(spec.name) && <div className="invalid-feedback">{errorFor(spec.name)}</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="product-funding-details-step">
      <h5 className="mb-1">Funding-Specific Details</h5>
      <p className="text-muted mb-4">
        This page changes automatically according to the funding product selected in the previous step.
      </p>

      <div className="card bg-light border mb-4">
        <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <div className="text-muted small text-uppercase fw-bold">Funding product</div>
            <div className="fs-5 fw-semibold">
              {product ? product.shortName : 'No funding product selected'}
            </div>
          </div>
          <div className="text-end small">
            <span className="badge bg-success rounded-circle p-1 me-1">&nbsp;</span>
            <span className="fw-bold">Dynamic page active</span>
            <div className="text-muted">
              {product ? `Showing fields for ${product.shortName}` : 'Select a product on the previous step'}
            </div>
          </div>
        </div>
      </div>

      {!product ? (
        <div className="alert alert-info mb-0" role="status">
          Select a funding product on the previous step to capture the details relevant to it.
        </div>
      ) : (
        <>
          <div className="alert alert-success border-start border-4 py-2 mb-4" role="status">
            <strong>{product.heading}</strong>
            <span className="ms-2">{product.blurb}</span>
          </div>

          <div className="row">
            {product.fields.map((spec, i) => renderField(spec, FUNDING_PRODUCT_COLUMN_SPANS[i] ?? 6))}
          </div>

          <div className="card bg-light border mt-3">
            <div className="card-body">
              <div className="row">
                <div className="col-md-4">
                  <div className="fw-bold">Documents this selection will request</div>
                  <small className="text-muted">
                    Requested later in the application, once the funding product is confirmed.
                  </small>
                </div>
                <div className="col-md-8">
                  <div className="row">
                    {product.documents.map((doc) => (
                      <div className="col-sm-6 mb-1" key={doc.type + doc.label}>
                        <span className="text-success me-1">&#10003;</span>
                        {doc.label}
                        {!doc.required && <small className="text-muted"> (where applicable)</small>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProductFundingDetailsStep;
